import { useState } from "react";
import axios from "axios";
import { Eye, EyeOff, Lock, Shield } from "lucide-react";
import "./ResetPassword.css";
import config from "../../config";

axios.defaults.baseURL = config.BASE_URL;

const ResetPassword = ({ onClose }) => {
  const [username, setUsername] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [business, setBusiness] = useState("");

  // Error states
  const [businessError, setBusinessError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [retypePasswordError, setRetypePasswordError] = useState("");

  // Processing states
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [progress, setProgress] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setBusinessError("");
    setUsernameError("");
    setCurrentPasswordError("");
    setNewPasswordError("");
    setRetypePasswordError("");

    let hasError = false;

    // Validate fields
    if (!business) {
      setBusinessError("Please select a business");
      hasError = true;
    }
    if (!username) {
      setUsernameError("Username is required");
      hasError = true;
    }
    if (!currentPassword) {
      setCurrentPasswordError("Current password is required");
      hasError = true;
    }
    if (!newPassword) {
      setNewPasswordError("New password is required");
      hasError = true;
    }
    if (!retypePassword) {
      setRetypePasswordError("Please retype new password");
      hasError = true;
    }

    if (hasError) return;

    // Check if new password is same as current password
    if (newPassword === currentPassword) {
      setNewPasswordError(
        "New password must be different from current password",
      );
      hasError = true;
    }

    // Check password match
    if (newPassword !== retypePassword) {
      setRetypePasswordError("Passwords do not match");
      hasError = true;
    }

    // Validate password strength
    const passwordRegex = new RegExp(
      '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()+_\\-={}\\[\\]|:;"<>,.?/])[A-Za-z\\d!@#$%^&*()+_\\-={}\\[\\]|:;"<>,.?/]{8,}$',
    );

    if (!passwordRegex.test(newPassword)) {
      setNewPasswordError(
        "Password must be at least 8 characters with uppercase, lowercase, number, and special character.",
      );
      hasError = true;
    }

    if (hasError) return;

    // Start processing
    setIsProcessing(true);
    setStatusMessage("Securely updating your password...");
    setProgress(25);

    try {
      const response = await axios.post(
        "/reset-password",
        { username, currentPassword, newPassword, retypePassword, business },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      setProgress(50);

      setTimeout(() => {
        setStatusMessage("Password updated successfully!");
        setProgress(80);

        setTimeout(() => {
          setStatusMessage("Please login with your new password");
          setProgress(100);

          setTimeout(() => {
            setIsProcessing(false);
            onClose();
          }, 2000);
        }, 1500);
      }, 1000);

      console.log("Response data:", response.data);
    } catch (error) {
      setIsProcessing(false);
      setStatusMessage("");
      setProgress(0);

      if (error.response?.data?.message) {
        const errorMsg = error.response.data.message;

        if (
          errorMsg.toLowerCase().includes("current password") ||
          errorMsg.toLowerCase().includes("incorrect password")
        ) {
          setCurrentPasswordError("🔒 " + errorMsg);
        } else if (errorMsg.toLowerCase().includes("user not found")) {
          setUsernameError("👤 " + errorMsg);
        } else if (errorMsg.toLowerCase().includes("business")) {
          setBusinessError("🏢 " + errorMsg);
        } else {
          alert("⚠️ " + errorMsg);
        }
      } else {
        alert("⚠️ Error updating password. Please try again.");
      }
    }
  };

  return (
    <>
      {isProcessing && (
        <div className="reset-fullscreen-overlay">
          <div className="reset-progress-container">
            <div className="reset-progress-visual">
              <div className="reset-security-animation"></div>
              <div className="reset-progress-ring"></div>
            </div>
            <div className="reset-status-message">{statusMessage}</div>
            <div className="reset-progress-bar">
              <div
                className="reset-progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="reset-progress-percentage">{progress}%</div>
          </div>
        </div>
      )}

      {!isProcessing && (
        <>
          <div className="reset-password-background">
            <div className="security-shield">
              <div className="shield-icon">🔒</div>
              <div className="security-lock lock-1"></div>
              <div className="security-lock lock-2"></div>
              <div className="security-lock lock-3"></div>
            </div>
            <div className="security-message security-msg-1">
              🔐 Enterprise-grade Security
              <br />
              End-to-end Encryption
            </div>
            <div className="security-message security-msg-2">
              ✅ Password Strength Analyzed
              <br />
              Real-time Validation
            </div>
            <div className="security-message security-msg-3">
              🛡️ Multi-layer Protection
              <br />
              Secure Authentication
            </div>
          </div>

          <div className="reset-password-overlay" onClick={onClose}>
            <div
              className="reset-password-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Reset Password</h2>
                <p>Update your password securely. All fields are required.</p>
              </div>

              <form
                autoComplete="off"
                onSubmit={handleSubmit}
                className="reset-password-form"
              >
                <div className="modal-form-group">
                  <label htmlFor="business">Business</label>
                  <select
                    id="business"
                    value={business}
                    onChange={(e) => {
                      setBusiness(e.target.value);
                      setBusinessError("");
                    }}
                    required
                    className={`modal-input ${businessError ? "modal-input-error" : ""}`}
                    disabled={isProcessing}
                  >
                    <option value="">Select business</option>
                    <option value="shree">Business 1</option>
                    <option value="lalchand">Business 2</option>
                  </select>
                  {businessError && (
                    <span className="modal-error-message">
                      <Shield size={14} /> {businessError}
                    </span>
                  )}
                </div>

                <div className="modal-form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    autoComplete="off"
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setUsernameError("");
                    }}
                    required
                    placeholder="Enter your username"
                    className={`modal-input ${usernameError ? "modal-input-error" : ""}`}
                    disabled={isProcessing}
                  />
                  {usernameError && (
                    <span className="modal-error-message">
                      <Shield size={14} /> {usernameError}
                    </span>
                  )}
                </div>

                <div className="modal-form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        setCurrentPasswordError("");
                      }}
                      required
                      placeholder="Enter current password"
                      className={`modal-input ${currentPasswordError ? "modal-input-error" : ""}`}
                      disabled={isProcessing}
                    />
                    <button
                      type="button"
                      className="modal-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isProcessing}
                    ></button>
                  </div>
                  {currentPasswordError && (
                    <span className="modal-error-message">
                      <Lock size={14} /> {currentPasswordError}
                    </span>
                  )}
                </div>

                <div className="modal-form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setNewPasswordError("");
                      }}
                      required
                      placeholder="Enter new password"
                      className={`modal-input ${newPasswordError ? "modal-input-error" : ""}`}
                      disabled={isProcessing}
                    />
                    <button
                      type="button"
                      className="modal-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isProcessing}
                    ></button>
                  </div>
                  {newPasswordError && (
                    <span className="modal-error-message">
                      <Lock size={14} /> {newPasswordError}
                    </span>
                  )}
                </div>

                <div className="modal-form-group">
                  <label htmlFor="retypePassword">Confirm New Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="retypePassword"
                      value={retypePassword}
                      onChange={(e) => {
                        setRetypePassword(e.target.value);
                        setRetypePasswordError("");
                      }}
                      required
                      placeholder="Retype new password"
                      className={`modal-input ${retypePasswordError ? "modal-input-error" : ""}`}
                      disabled={isProcessing}
                    />
                    <button
                      type="button"
                      className="modal-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isProcessing}
                    ></button>
                  </div>
                  {retypePasswordError && (
                    <span className="modal-error-message">
                      <Lock size={14} /> {retypePasswordError}
                    </span>
                  )}
                </div>

                <div
                  className="show-password-link"
                  onClick={() =>
                    !isProcessing && setShowPassword(!showPassword)
                  }
                  style={{ cursor: isProcessing ? "not-allowed" : "pointer" }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showPassword ? "Hide Password" : "Show Password"}
                </div>

                <div className="modal-buttons">
                  <button
                    type="submit"
                    className="modal-submit-button"
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Securing..." : "Update Password"}
                  </button>
                  <button
                    type="button"
                    className="modal-cancel-button"
                    onClick={onClose}
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ResetPassword;
